import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	forwardRef,
	inject,
	Input,
	OnChanges,
	Output,
	Renderer2,
	SimpleChanges,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

import {
	IMyCalendarDay,
	IMyCalendarMonth,
	IMyCalendarViewChanged,
	IMyCalendarYear,
	IMyDate,
	IMyDateModel,
	IMyDateRange,
	IMyDayLabels,
	IMyInputFieldChanged,
	IMyInputFocusBlur,
	IMyMarkedDate,
	IMyMarkedDates,
	IMyMonth,
	IMyMonthLabels,
	IMyOptions,
	IMyWeek,
} from "./interfaces/index";
import { LocaleService } from "./services/my-date-picker.locale.service";
import { UtilService } from "./services/my-date-picker.util.service";

export const MYDP_VALUE_ACCESSOR: any = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => MyDatePicker),
	multi: true,
};

const enum CalToggle {
	Open = 1,
	CloseByDateSel = 2,
	CloseByCalBtn = 3,
	CloseByOutClick = 4,
	CloseByEsc = 5,
	CloseByApi = 6,
}
const enum Year {
	min = 1000,
	max = 9999,
}
const enum InputFocusBlur {
	focus = 1,
	blur = 2,
}
const enum KeyCode {
	enter = 13,
	esc = 27,
	space = 32,
}
const enum MonthId {
	prev = 1,
	curr = 2,
	next = 3,
}

const MM = "mm";
const MMM = "mmm";
const DD = "dd";
const YYYY = "yyyy";

@Component({
	selector: "ic-date-picker",
	styleUrls: ["./my-date-picker.component.scss"],
	templateUrl: "./my-date-picker.component.html",
	providers: [LocaleService, UtilService, MYDP_VALUE_ACCESSOR],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class MyDatePicker implements OnChanges, ControlValueAccessor {
	elem = inject(ElementRef);
	private renderer = inject(Renderer2);
	private cdr = inject(ChangeDetectorRef);
	private localeService = inject(LocaleService);
	private utilService = inject(UtilService);

	@Input() options: IMyOptions;
	@Input() locale: string;
	@Input() defaultMonth: string;
	@Input() selDate: string | IMyDate;
	@Input() placeholder: string;
	@Input() selector: number;
	@Input() disabled: boolean;
	@Output() dateChanged: EventEmitter<IMyDateModel> = new EventEmitter<IMyDateModel>();
	@Output() inputFieldChanged: EventEmitter<IMyInputFieldChanged> = new EventEmitter<IMyInputFieldChanged>();
	@Output() calendarViewChanged: EventEmitter<IMyCalendarViewChanged> = new EventEmitter<IMyCalendarViewChanged>();
	@Output() calendarToggle: EventEmitter<number> = new EventEmitter<number>();
	@Output() inputFocusBlur: EventEmitter<IMyInputFocusBlur> = new EventEmitter<IMyInputFocusBlur>();
	@ViewChild("selectorEl", { static: false }) selectorEl: ElementRef;
	@ViewChild("inputBoxEl", { static: false }) inputBoxEl: ElementRef;

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	onChangeCb: (_: any) => void = () => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	onTouchedCb: () => void = () => {};

	showSelector = false;
	visibleMonth: IMyMonth = { monthTxt: "", monthNbr: 0, year: 0 };
	selectedMonth: IMyMonth = { monthTxt: "", monthNbr: 0, year: 0 };
	selectedDate: IMyDate = { year: 0, month: 0, day: 0 };
	weekDays: string[] = [];
	dates: IMyWeek[] = [];
	months: IMyCalendarMonth[][] = [];
	years: IMyCalendarYear[][] = [];
	selectionDayTxt = "";
	invalidDate = false;
	disableTodayBtn = false;
	dayIdx = 0;
	currWeek: number = this.utilService.getWeekNumber(this.getToday());

	selectMonth = false;
	selectYear = false;

	prevMonthDisabled = false;
	nextMonthDisabled = false;
	prevYearDisabled = false;
	nextYearDisabled = false;
	prevYearsDisabled = false;
	nextYearsDisabled = false;

	prevMonthId: number = MonthId.prev;
	currMonthId: number = MonthId.curr;
	nextMonthId: number = MonthId.next;

	// Default options
	opts: IMyOptions = {
		dayLabels: {} as IMyDayLabels,
		monthLabels: {} as IMyMonthLabels,
		dateFormat: "" as string,
		showTodayBtn: true as boolean,
		todayBtnTxt: "" as string,
		firstDayOfWeek: "" as string,
		satHighlight: false as boolean,
		sunHighlight: true as boolean,
		highlightDates: [] as IMyDate[],
		markCurrentDay: true as boolean,
		markCurrentWeek: true as boolean,
		markCurrentMonth: true as boolean,
		markCurrentYear: true as boolean,
		disableUntil: { year: 0, month: 0, day: 0 } as IMyDate,
		disableSince: { year: 0, month: 0, day: 0 } as IMyDate,
		disableDays: [] as IMyDate[],
		enableDays: [] as IMyDate[],
		markDates: [] as IMyMarkedDates[],
		markWeekends: {} as IMyMarkedDate,
		disableDateRanges: [] as IMyDateRange[],
		disableWeekends: false as boolean,
		disableWeekdays: [] as string[],
		showWeekNumbers: false as boolean,
		height: "34px" as string,
		width: "100%" as string,
		selectionTxtFontSize: "14px" as string,
		selectorHeight: "232px" as string,
		selectorWidth: "252px" as string,
		allowDeselectDate: false as boolean,
		inline: false as boolean,
		showClearDateBtn: true as boolean,
		showDecreaseDateBtn: false as boolean,
		showIncreaseDateBtn: false as boolean,
		alignSelectorRight: false as boolean,
		openSelectorTopOfInput: false as boolean,
		indicateInvalidDate: true as boolean,
		editableDateField: true as boolean,
		monthSelector: true as boolean,
		yearSelector: true as boolean,
		disableHeaderButtons: true as boolean,
		minYear: Year.min as number,
		maxYear: Year.max as number,
		componentDisabled: false as boolean,
		showSelectorArrow: true as boolean,
		showInputField: true as boolean,
		openSelectorOnInputClick: false as boolean,
		ariaLabelInputField: "Date input field" as string,
		ariaLabelClearDate: "Clear Date" as string,
		ariaLabelDecreaseDate: "Decrease Date" as string,
		ariaLabelIncreaseDate: "Increase Date" as string,
		ariaLabelOpenCalendar: "Open Calendar" as string,
		ariaLabelPrevMonth: "Previous Month" as string,
		ariaLabelNextMonth: "Next Month" as string,
		ariaLabelPrevYear: "Previous Year" as string,
		ariaLabelNextYear: "Next Year" as string,
		selectWeekOnly: false as boolean,
		selectMonthOnly: false as boolean,
		selectYearOnly: false as boolean,
		selectYearText: "Select a year" as string,
	};

	constructor() {
		const renderer = this.renderer;

		this.setLocaleOptions();
		renderer.listen("document", "click", (event) => {
			if (
				this.showSelector
				&& event.target
				&& this.elem.nativeElement !== event.target
				&& !this.elem.nativeElement.contains(event.target)
			) {
				this.showSelector = false;
				this.calendarToggle.emit(CalToggle.CloseByOutClick);
			}
			if (this.opts.monthSelector || this.opts.yearSelector) {
				this.resetMonthYearSelect();
			}
		});
	}

	setLocaleOptions(): void {
		const opts = this.localeService.getLocaleOptions(this.locale);
		Object.keys(opts).forEach((k) => {
			(this.opts as IMyOptions)[k] = opts[k];
		});
	}

	setOptions(): void {
		if (this.options !== undefined) {
			Object.keys(this.options).forEach((k) => {
				(this.opts as IMyOptions)[k] = this.options[k];
			});
		}
		if (this.opts.minYear < Year.min) {
			this.opts.minYear = Year.min;
		}
		if (this.opts.maxYear > Year.max) {
			this.opts.maxYear = Year.max;
		}
		if (this.disabled !== undefined) {
			this.opts.componentDisabled = this.disabled;
		}
	}

	getSelectorTopPosition(): string {
		if (this.opts.openSelectorTopOfInput) {
			return `${this.elem.nativeElement.children[0].offsetHeight}px`;
		}
	}

	resetMonthYearSelect(): void {
		this.selectMonth = this.opts.selectMonthOnly;
		this.selectYear = false;
	}

	onSelectMonthClicked(event: any): void {
		event.stopPropagation();
		this.selectMonth = !this.selectMonth;
		this.selectYear = false;
		if (this.selectMonth) {
			const today = this.getToday();
			this.months.length = 0;
			for (let i = 1; i <= 12; i += 3) {
				const row: IMyCalendarMonth[] = [];
				for (let j = i; j < i + 3; j++) {
					const disabled =
						this.utilService.isMonthDisabledByDisableUntil(
							{ year: this.visibleMonth.year, month: j, day: this.daysInMonth(j, this.visibleMonth.year) },
							this.opts.disableUntil,
						)
						|| this.utilService.isMonthDisabledByDisableSince(
							{ year: this.visibleMonth.year, month: j, day: 1 },
							this.opts.disableSince,
						);
					row.push({
						nbr: j,
						name: this.opts.monthLabels[j],
						currMonth: j === today.month && this.visibleMonth.year === today.year,
						selected: j === this.visibleMonth.monthNbr,
						disabled,
					});
				}
				this.months.push(row);
			}
		}
		this.cdr.markForCheck();
	}

	onMonthCellClicked(cell: IMyCalendarMonth): void {
		const mc = cell.nbr !== this.visibleMonth.monthNbr;
		this.visibleMonth = { monthTxt: this.monthText(cell.nbr), monthNbr: cell.nbr, year: this.visibleMonth.year };
		if (!this.options.selectMonthOnly) {
			this.generateCalendar(cell.nbr, this.visibleMonth.year, mc);
			this.selectMonth = false;
			this.selectorEl.nativeElement.focus();
		} else {
			const date: IMyDate = {
				day: 1,
				month: cell.nbr,
				year: this.visibleMonth.year,
			};

			const dateModel = this.getDateModel(date);
			this.dateChanged.emit(dateModel);
		}
	}

	onWeekCellClicked(week: IMyWeek): void {
		const date = week.week[0].dateObj;
		date.week = week.weekNbr;
		const dateModel = this.getDateModel(date);
		this.dateChanged.emit(dateModel);
	}

	onMonthCellKeyDown(event: any, cell: IMyCalendarMonth) {
		if ((event.keyCode === KeyCode.enter || event.keyCode === KeyCode.space) && !cell.disabled) {
			event.preventDefault();
			this.onMonthCellClicked(cell);
		}
	}

	onSelectYearClicked(event: any): void {
		event.stopPropagation();
		this.selectYear = !this.selectYear;
		this.selectMonth = false;
		if (this.selectYear) {
			this.generateYears(this.visibleMonth.year);
		}
	}

	onYearCellClicked(cell: IMyCalendarYear): void {
		if (this.options.selectYearOnly) {
			const dateModel = this.getDateModel({
				day: 1,
				month: 1,
				year: cell.year,
			});
			this.dateChanged.emit(dateModel);
			this.cdr.markForCheck();
			return;
		}

		this.visibleMonth = { monthTxt: this.visibleMonth.monthTxt, monthNbr: this.visibleMonth.monthNbr, year: cell.year };

		if (this.options.selectMonthOnly) {
			this.selectYear = false;
			this.selectMonth = true;
			this.nextYearDisabled = cell.year >= new Date().getFullYear();
			this.setUpMonthSelection();
			this.cdr.markForCheck();
			return;
		}

		const yc = cell.year !== this.visibleMonth.year;
		this.generateCalendar(this.visibleMonth.monthNbr, cell.year, yc);
		this.selectYear = false;
		this.selectorEl.nativeElement.focus();
		this.cdr.markForCheck();
	}

	onYearCellKeyDown(event: any, cell: IMyCalendarYear) {
		if ((event.keyCode === KeyCode.enter || event.keyCode === KeyCode.space) && !cell.disabled) {
			event.preventDefault();
			this.onYearCellClicked(cell);
		}
	}

	onPrevYears(event: any, year: number): void {
		event.stopPropagation();
		this.generateYears(year - 25);
	}

	onNextYears(event: any, year: number): void {
		event.stopPropagation();
		this.generateYears(year + 25);
	}

	generateYears(year: number): void {
		this.years.length = 0;
		const today = this.getToday();
		for (let i = year; i <= 20 + year; i += 5) {
			const row: IMyCalendarYear[] = [];
			for (let j = i; j < i + 5; j++) {
				const disabled = j < this.opts.disableUntil.year || j > this.opts.disableSince.year;
				const minMax = j < this.opts.minYear || j > this.opts.maxYear;
				row.push({
					year: j,
					currYear: j === today.year,
					selected: j === this.selectedDate.year,
					disabled: disabled || minMax,
				});
			}
			this.years.push(row);
		}
		this.prevYearsDisabled = year < this.opts.disableUntil.year;
		this.nextYearsDisabled = year > this.opts.disableSince.year;
		this.cdr.markForCheck();
	}

	setUpYearSelection() {
		const date = new Date();
		this.visibleMonth.monthNbr = date.getMonth() + 1;
		this.visibleMonth.year = date.getFullYear();
		this.selectYear = true;
		if (this.selectYear) {
			this.generateYears(this.visibleMonth.year);
		}
		this.cdr.markForCheck();
	}

	setUpMonthSelection() {
		const date = new Date();
		this.visibleMonth.monthNbr = date.getMonth() + 1;
		this.visibleMonth.year = this.visibleMonth.year || date.getFullYear();
		this.selectYear = false;
		this.selectMonth = true;
		if (this.selectMonth) {
			const today = this.getToday();
			this.months.length = 0;
			for (let i = 1; i <= 12; i += 3) {
				const row: IMyCalendarMonth[] = [];
				for (let j = i; j < i + 3; j++) {
					const disabled =
						this.utilService.isMonthDisabledByDisableUntil(
							{ year: this.visibleMonth.year, month: j, day: this.daysInMonth(j, this.visibleMonth.year) },
							this.opts.disableUntil,
						)
						|| this.utilService.isMonthDisabledByDisableSince(
							{ year: this.visibleMonth.year, month: j, day: 1 },
							this.opts.disableSince,
						);
					row.push({
						nbr: j,
						name: this.opts.monthLabels[j],
						currMonth: j === today.month && this.visibleMonth.year === today.year,
						selected: j === this.selectedDate.month && this.visibleMonth.year === this.selectedDate.year,
						disabled,
					});
				}
				this.months.push(row);
			}
		}
		this.cdr.markForCheck();
	}

	onUserDateInput(value: string): void {
		if (value.length === 0) {
			if (this.utilService.isInitializedDate(this.selectedDate)) {
				this.clearDate();
			} else {
				this.invalidInputFieldChanged(value);
			}
		} else {
			const date = this.utilService.isDateValid(
				value,
				this.opts.dateFormat,
				this.opts.minYear,
				this.opts.maxYear,
				this.opts.disableUntil,
				this.opts.disableSince,
				this.opts.disableWeekends,
				this.opts.disableWeekdays,
				this.opts.disableDays,
				this.opts.disableDateRanges,
				this.opts.monthLabels,
				this.opts.enableDays,
			);
			if (date.day !== 0 && date.month !== 0 && date.year !== 0) {
				if (!this.utilService.isSameDate(date, this.selectedDate)) {
					this.selectDate(date, CalToggle.CloseByDateSel);
				} else {
					this.updateDateValue(date, false);
				}
			} else {
				this.invalidInputFieldChanged(value);
			}
		}
		this.cdr.markForCheck();
	}

	onFocusInput(event: any): void {
		this.inputFocusBlur.emit({ reason: InputFocusBlur.focus, value: event.target.value });
	}

	onBlurInput(event: any): void {
		this.selectionDayTxt = event.target.value;
		this.onTouchedCb();
		this.inputFocusBlur.emit({ reason: InputFocusBlur.blur, value: event.target.value });
	}

	onCloseSelector(event: any): void {
		if (event.keyCode === KeyCode.esc && this.showSelector && !this.opts.inline) {
			this.calendarToggle.emit(CalToggle.CloseByEsc);
			this.showSelector = false;
		}
	}

	invalidInputFieldChanged(value: string): void {
		this.invalidDate = value.length > 0;
		this.inputFieldChanged.emit({ value, dateFormat: this.opts.dateFormat, valid: false });
		this.onChangeCb(null);
		this.onTouchedCb();
	}

	isTodayDisabled(): void {
		this.disableTodayBtn = this.utilService.isDisabledDay(
			this.getToday(),
			this.opts.minYear,
			this.opts.maxYear,
			this.opts.disableUntil,
			this.opts.disableSince,
			this.opts.disableWeekends,
			this.opts.disableWeekdays,
			this.opts.disableDays,
			this.opts.disableDateRanges,
			this.opts.enableDays,
		);
		this.cdr.markForCheck();
	}

	parseOptions(): void {
		if (this.locale) {
			this.setLocaleOptions();
		}
		this.setOptions();
		const weekDays = this.utilService.getWeekDays();
		this.isTodayDisabled();
		// Keep parseOptions idempotent when called repeatedly from tests and input updates.
		this.weekDays.length = 0;
		this.dayIdx = weekDays.indexOf(this.opts.firstDayOfWeek);
		if (this.dayIdx !== -1) {
			let idx = this.dayIdx;
			for (let i = 0; i < weekDays.length; i++) {
				this.weekDays.push(this.opts.dayLabels[weekDays[idx]]);
				idx = weekDays[idx] === "sa" ? 0 : idx + 1;
			}
		}
		this.cdr.markForCheck();
	}

	writeValue(value: any): void {
		if (value && (value.date || value.jsdate || value.formatted)) {
			this.selectedDate = value.date
				? this.parseSelectedDate(value.date)
				: value.jsdate
					? this.parseSelectedDate(this.jsDateToMyDate(value.jsdate))
					: this.parseSelectedDate(value.formatted);
			const cvc =
				this.visibleMonth.year !== this.selectedDate.year || this.visibleMonth.monthNbr !== this.selectedDate.month;
			if (cvc) {
				this.visibleMonth = {
					monthTxt: this.opts.monthLabels[this.selectedDate.month],
					monthNbr: this.selectedDate.month,
					year: this.selectedDate.year,
				};
				this.generateCalendar(this.selectedDate.month, this.selectedDate.year, cvc);
			}
			if (!this.opts.inline) {
				this.updateDateValue(this.selectedDate, false);
			}
		} else if (value === null || value === "") {
			if (!this.opts.inline) {
				this.updateDateValue({ year: 0, month: 0, day: 0 }, true);
			} else {
				this.selectedDate = { year: 0, month: 0, day: 0 };
			}
		}
	}

	setDisabledState(disabled: boolean): void {
		this.opts.componentDisabled = disabled;
	}

	registerOnChange(fn: (v: unknown) => void) {
		this.onChangeCb = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouchedCb = fn;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.hasOwnProperty("selector")) {
			const s = changes.selector.currentValue;
			if (typeof s === "object") {
				if (s.open) {
					this.showSelector = true;
					this.openSelector(CalToggle.Open);
				} else {
					this.showSelector = false;
					this.closeSelector(CalToggle.CloseByApi);
				}
			} else if (s > 0) {
				this.openBtnClicked();
			}
		}

		if (changes.hasOwnProperty("placeholder")) {
			this.placeholder = changes.placeholder.currentValue;
		}

		if (changes.hasOwnProperty("locale")) {
			this.locale = changes.locale.currentValue;
		}

		if (changes.hasOwnProperty("disabled")) {
			this.disabled = changes.disabled.currentValue;
		}

		if (changes.hasOwnProperty("options")) {
			this.options = changes.options.currentValue;
		}

		this.parseOptions();

		let dmChange = false;
		if (changes.hasOwnProperty("defaultMonth")) {
			let dm = changes.defaultMonth.currentValue;
			if (typeof dm === "object") {
				dm = dm.defMonth;
			}
			if (dm !== null && dm !== undefined && dm !== "") {
				this.selectedMonth = this.parseSelectedMonth(dm);
			} else {
				this.selectedMonth = { monthTxt: "", monthNbr: 0, year: 0 };
			}
			dmChange = true;
		}

		if (changes.hasOwnProperty("selDate")) {
			const sd: any = changes.selDate;
			if (
				sd.currentValue !== null
				&& sd.currentValue !== undefined
				&& sd.currentValue !== ""
				&& Object.keys(sd.currentValue).length !== 0
			) {
				this.selectedDate = this.parseSelectedDate(sd.currentValue);
				setTimeout(() => {
					this.onChangeCb(this.getDateModel(this.selectedDate));
				});
			} else {
				// Do not clear on init
				if (!sd.isFirstChange()) {
					this.clearDate();
				}
			}
		}
		if ((this.visibleMonth.year === 0 && this.visibleMonth.monthNbr === 0) || dmChange) {
			this.setVisibleMonth();
		} else {
			this.visibleMonth.monthTxt = this.opts.monthLabels[this.visibleMonth.monthNbr];
			this.generateCalendar(this.visibleMonth.monthNbr, this.visibleMonth.year, false);
		}
		if (this.opts.selectMonthOnly) {
			this.setUpMonthSelection();
		}
		if (this.opts.selectYearOnly) {
			this.setUpYearSelection();
		}
	}

	removeBtnClicked(): void {
		// Remove date button clicked
		this.clearDate();
		if (this.showSelector) {
			this.calendarToggle.emit(CalToggle.CloseByCalBtn);
		}
		this.showSelector = false;
	}

	onDecreaseBtnClicked(): void {
		// Decrease date button clicked
		this.decreaseIncreaseDate(true);
	}

	onIncreaseBtnClicked(): void {
		// Increase date button clicked
		this.decreaseIncreaseDate(false);
	}

	openBtnClicked(): void {
		// Open selector button clicked
		this.showSelector = !this.showSelector;
		this.cdr.markForCheck();
		if (this.showSelector) {
			this.openSelector(CalToggle.Open);
		} else {
			this.closeSelector(CalToggle.CloseByCalBtn);
		}
	}

	openSelector(reason: number): void {
		this.setVisibleMonth();
		this.calendarToggle.emit(reason);
	}

	closeSelector(reason: number): void {
		this.calendarToggle.emit(reason);
	}

	setVisibleMonth(): void {
		// Sets visible month of calendar
		// eslint-disable-next-line no-useless-assignment
		let y = 0,
			// eslint-disable-next-line no-useless-assignment
			m = 0;
		if (!this.utilService.isInitializedDate(this.selectedDate)) {
			if (this.selectedMonth.year === 0 && this.selectedMonth.monthNbr === 0) {
				const today = this.getToday();
				y = today.year;
				m = today.month;
			} else {
				y = this.selectedMonth.year;
				m = this.selectedMonth.monthNbr;
			}
		} else {
			y = this.selectedDate.year;
			m = this.selectedDate.month;
		}
		this.visibleMonth = { monthTxt: this.opts.monthLabels[m], monthNbr: m, year: y };

		// Create current month
		this.generateCalendar(m, y, true);
	}

	onPrevMonth(): void {
		// Previous month from calendar
		const d = this.getDate(this.visibleMonth.year, this.visibleMonth.monthNbr, 1);
		d.setMonth(d.getMonth() - 1);

		const y = d.getFullYear();
		const m = d.getMonth() + 1;

		this.visibleMonth = { monthTxt: this.monthText(m), monthNbr: m, year: y };
		this.generateCalendar(m, y, true);
	}

	onNextMonth(): void {
		// Next month from calendar
		const d = this.getDate(this.visibleMonth.year, this.visibleMonth.monthNbr, 1);
		d.setMonth(d.getMonth() + 1);

		const y = d.getFullYear();
		const m = d.getMonth() + 1;

		this.visibleMonth = { monthTxt: this.monthText(m), monthNbr: m, year: y };
		this.generateCalendar(m, y, true);
	}

	onPrevYear(): void {
		// Previous year from calendar
		this.visibleMonth.year--;
		this.generateCalendar(this.visibleMonth.monthNbr, this.visibleMonth.year, true);
	}

	onNextYear(): void {
		// Next year from calendar
		this.visibleMonth.year++;
		this.generateCalendar(this.visibleMonth.monthNbr, this.visibleMonth.year, true);
	}

	onTodayClicked(): void {
		// Today button clicked
		const today = this.getToday();
		this.selectDate(today, CalToggle.CloseByDateSel);
		if ((this.opts.inline && today.year !== this.visibleMonth.year) || today.month !== this.visibleMonth.monthNbr) {
			this.visibleMonth = { monthTxt: this.opts.monthLabels[today.month], monthNbr: today.month, year: today.year };
			this.generateCalendar(today.month, today.year, true);
		}
	}

	onCellClicked(cell: any): void {
		// Cell clicked on the calendar
		if (cell.cmo === this.prevMonthId) {
			// Previous month day
			this.onPrevMonth();
		} else if (cell.cmo === this.currMonthId) {
			// Current month day - if date is already selected clear it
			if (this.opts.allowDeselectDate && this.utilService.isSameDate(cell.dateObj, this.selectedDate)) {
				this.clearDate();
			} else {
				this.selectDate(cell.dateObj, CalToggle.CloseByDateSel);
			}
		} else if (cell.cmo === this.nextMonthId) {
			// Next month day
			this.onNextMonth();
		}
		this.resetMonthYearSelect();
	}

	onCellKeyDown(event: any, cell: any) {
		// Cell keyboard handling
		if ((event.keyCode === KeyCode.enter || event.keyCode === KeyCode.space) && !cell.disabled) {
			event.preventDefault();
			this.onCellClicked(cell);
		}
	}

	clearDate(): void {
		// Clears the date and notifies parent using callbacks and value accessor
		const date: IMyDate = { year: 0, month: 0, day: 0 };
		this.dateChanged.emit({ date, jsdate: null, formatted: "", epoc: 0 });
		this.onChangeCb(null);
		this.onTouchedCb();
		this.updateDateValue(date, true);
		this.setFocusToInputBox();
	}

	decreaseIncreaseDate(decrease: boolean): void {
		// Decreases or increases the date depending on the parameter
		let date = this.selectedDate;
		if (this.utilService.isInitializedDate(date)) {
			const d = this.getDate(date.year, date.month, date.day);
			d.setDate(decrease ? d.getDate() - 1 : d.getDate() + 1);
			date = { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
		} else {
			date = this.getToday();
		}
		this.selectDate(date, CalToggle.CloseByCalBtn);
	}

	selectDate(date: IMyDate, closeReason: number): void {
		// Date selected, notifies parent using callbacks and value accessor
		const dateModel = this.getDateModel(date);
		this.dateChanged.emit(dateModel);
		this.onChangeCb(dateModel);
		this.onTouchedCb();
		this.updateDateValue(date, false);
		if (this.showSelector) {
			this.calendarToggle.emit(closeReason);
		}
		this.showSelector = false;
		this.setFocusToInputBox();
	}

	// TODO Fix timeout?
	setFocusToInputBox(): void {
		if (!this.opts.inline && this.opts.showInputField) {
			setTimeout(() => {
				this.inputBoxEl.nativeElement.focus();
			}, 100);
		}
	}

	updateDateValue(date: IMyDate, clear: boolean): void {
		// Updates date values
		this.selectedDate = date;
		this.selectionDayTxt = clear ? "" : this.formatDate(date);
		this.inputFieldChanged.emit({ value: this.selectionDayTxt, dateFormat: this.opts.dateFormat, valid: !clear });
		this.invalidDate = false;
	}

	getDateModel(date: IMyDate): IMyDateModel {
		// Creates a date model object from the given parameter
		return {
			date,
			jsdate: this.getDate(date.year, date.month, date.day),
			formatted: this.formatDate(date),
			epoc: Math.round(this.getTimeInMilliseconds(date) / 1000.0),
		};
	}

	preZero(val: string): string {
		// Prepend zero if smaller than 10
		return parseInt(val, 10) < 10 ? "0" + val : val;
	}

	formatDate(val: any): string {
		// Returns formatted date string, if mmm is part of dateFormat returns month as a string
		const formatted = this.opts.dateFormat.replace(YYYY, val.year).replace(DD, this.preZero(val.day));
		return this.opts.dateFormat.indexOf(MMM) !== -1
			? formatted.replace(MMM, this.monthText(val.month))
			: formatted.replace(MM, this.preZero(val.month));
	}

	monthText(m: number): string {
		// Returns month as a text
		return this.opts.monthLabels[m];
	}

	monthStartIdx(y: number, m: number): number {
		// Month start index
		const d = new Date();
		d.setDate(1);
		d.setMonth(m - 1);
		d.setFullYear(y);
		const idx = d.getDay() + this.sundayIdx();
		return idx >= 7 ? idx - 7 : idx;
	}

	daysInMonth(m: number, y: number): number {
		// Return number of days of current month
		return new Date(y, m, 0).getDate();
	}

	daysInPrevMonth(m: number, y: number): number {
		// Return number of days of the previous month
		const d = this.getDate(y, m, 1);
		d.setMonth(d.getMonth() - 1);
		return this.daysInMonth(d.getMonth() + 1, d.getFullYear());
	}

	isCurrDay(d: number, m: number, y: number, cmo: number, today: IMyDate): boolean {
		// Check is a given date the today
		return d === today.day && m === today.month && y === today.year && cmo === this.currMonthId;
	}

	getToday(): IMyDate {
		const date = new Date();
		return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
	}

	getTimeInMilliseconds(date: IMyDate): number {
		return this.getDate(date.year, date.month, date.day).getTime();
	}

	getWeekday(date: IMyDate): string {
		// Get weekday: su, mo, tu, we ...
		const weekDays = this.utilService.getWeekDays();
		return weekDays[this.utilService.getDayNumber(date)];
	}

	getDate(year: number, month: number, day: number): Date {
		// Creates a date object from given year, month and day
		return new Date(year, month - 1, day, 0, 0, 0, 0);
	}

	sundayIdx(): number {
		// Index of Sunday day
		return this.dayIdx > 0 ? 7 - this.dayIdx : 0;
	}

	generateCalendar(m: number, y: number, notifyChange: boolean): void {
		this.dates.length = 0;
		const today = this.getToday();
		const monthStart = this.monthStartIdx(y, m);
		const dInThisM = this.daysInMonth(m, y);
		const dInPrevM = this.daysInPrevMonth(m, y);

		let dayNbr = 1;
		let cmo = this.prevMonthId;
		for (let i = 1; i < 7; i++) {
			const week: IMyCalendarDay[] = [];
			if (i === 1) {
				// First week
				const pm = dInPrevM - monthStart + 1;
				// Previous month
				for (let j = pm; j <= dInPrevM; j++) {
					const date: IMyDate = { year: m === 1 ? y - 1 : y, month: m === 1 ? 12 : m - 1, day: j };
					week.push({
						dateObj: date,
						cmo,
						currDay: this.isCurrDay(j, m, y, cmo, today),
						disabled: this.utilService.isDisabledDay(
							date,
							this.opts.minYear,
							this.opts.maxYear,
							this.opts.disableUntil,
							this.opts.disableSince,
							this.opts.disableWeekends,
							this.opts.disableWeekdays,
							this.opts.disableDays,
							this.opts.disableDateRanges,
							this.opts.enableDays,
						),
						markedDate: this.utilService.isMarkedDate(date, this.opts.markDates, this.opts.markWeekends),
						highlight: this.utilService.isHighlightedDate(
							date,
							this.opts.sunHighlight,
							this.opts.satHighlight,
							this.opts.highlightDates,
						),
					});
				}

				cmo = this.currMonthId;
				// Current month
				const daysLeft = 7 - week.length;
				for (let j = 0; j < daysLeft; j++) {
					const date: IMyDate = { year: y, month: m, day: dayNbr };
					week.push({
						dateObj: date,
						cmo,
						currDay: this.isCurrDay(dayNbr, m, y, cmo, today),
						disabled: this.utilService.isDisabledDay(
							date,
							this.opts.minYear,
							this.opts.maxYear,
							this.opts.disableUntil,
							this.opts.disableSince,
							this.opts.disableWeekends,
							this.opts.disableWeekdays,
							this.opts.disableDays,
							this.opts.disableDateRanges,
							this.opts.enableDays,
						),
						markedDate: this.utilService.isMarkedDate(date, this.opts.markDates, this.opts.markWeekends),
						highlight: this.utilService.isHighlightedDate(
							date,
							this.opts.sunHighlight,
							this.opts.satHighlight,
							this.opts.highlightDates,
						),
					});
					dayNbr++;
				}
			} else {
				// Rest of the weeks
				for (let j = 1; j < 8; j++) {
					if (dayNbr > dInThisM) {
						// Next month
						dayNbr = 1;
						cmo = this.nextMonthId;
					}
					const date: IMyDate = {
						year: cmo === this.nextMonthId && m === 12 ? y + 1 : y,
						month: cmo === this.currMonthId ? m : cmo === this.nextMonthId && m < 12 ? m + 1 : 1,
						day: dayNbr,
					};
					week.push({
						dateObj: date,
						cmo,
						currDay: this.isCurrDay(dayNbr, m, y, cmo, today),
						disabled: this.utilService.isDisabledDay(
							date,
							this.opts.minYear,
							this.opts.maxYear,
							this.opts.disableUntil,
							this.opts.disableSince,
							this.opts.disableWeekends,
							this.opts.disableWeekdays,
							this.opts.disableDays,
							this.opts.disableDateRanges,
							this.opts.enableDays,
						),
						markedDate: this.utilService.isMarkedDate(date, this.opts.markDates, this.opts.markWeekends),
						highlight: this.utilService.isHighlightedDate(
							date,
							this.opts.sunHighlight,
							this.opts.satHighlight,
							this.opts.highlightDates,
						),
					});
					dayNbr++;
				}
			}
			const weekNbr =
				this.opts.showWeekNumbers && this.opts.firstDayOfWeek === "mo"
					? this.utilService.getWeekNumber(week[0].dateObj)
					: 0;
			this.dates.push({ week, weekNbr });
		}

		this.setHeaderBtnDisabledState(m, y);

		if (notifyChange) {
			// Notify parent
			this.calendarViewChanged.emit({
				year: y,
				month: m,
				first: { number: 1, weekday: this.getWeekday({ year: y, month: m, day: 1 }) },
				last: { number: dInThisM, weekday: this.getWeekday({ year: y, month: m, day: dInThisM }) },
			});
		}

		if (y !== new Date().getFullYear()) {
			this.months = this.months.map((monthRow) =>
				monthRow.map((row) => {
					return {
						...row,
						disabled: false,
						currMonth: today.month === row.nbr && today.year === y,
						selected: this.selectedDate.month === row.nbr && y === this.selectedDate.year,
					};
				}),
			);

			this.nextYearDisabled = false;
		}

		if (y === new Date().getFullYear()) {
			const currentMonth = new Date().getMonth() + 1;
			this.months = this.months.map((monthRow) =>
				monthRow.map((row) => {
					return {
						...row,
						disabled: row.nbr > currentMonth,
						currMonth: today.month === row.nbr && today.year === y,
						selected: this.selectedDate.month === row.nbr && y === this.selectedDate.year,
					};
				}),
			);

			this.nextYearDisabled = true;
		}

		this.cdr.markForCheck();
	}

	parseSelectedDate(selDate: any): IMyDate {
		// Parse date value - it can be string or IMyDate object
		let date: IMyDate = { day: 0, month: 0, week: 0, year: 0 };
		if (typeof selDate === "string") {
			const sd = selDate as string;
			let df = this.opts.dateFormat;

			date.month =
				df.indexOf(MMM) !== -1
					? this.utilService.parseDatePartMonthName(df, sd, MMM, this.opts.monthLabels)
					: this.utilService.parseDatePartNumber(df, sd, MM);

			if (df.indexOf(MMM) !== -1 && this.opts.monthLabels[date.month]) {
				df = this.utilService.changeDateFormat(df, this.opts.monthLabels[date.month].length);
			}

			date.day = this.utilService.parseDatePartNumber(df, sd, DD);
			date.year = this.utilService.parseDatePartNumber(df, sd, YYYY);
		} else if (typeof selDate === "object") {
			date = selDate;
		}
		date.week = this.utilService.getWeekNumber(date);
		this.selectionDayTxt = this.formatDate(date);
		return date;
	}

	jsDateToMyDate(date: Date): IMyDate {
		return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
	}

	parseSelectedMonth(ms: string): IMyMonth {
		return this.utilService.parseDefaultMonth(ms);
	}

	setHeaderBtnDisabledState(m: number, y: number): void {
		let dpm = false;
		let dpy = false;
		let dnm = false;
		let dny = false;
		if (this.opts.disableHeaderButtons) {
			dpm = this.utilService.isMonthDisabledByDisableUntil(
				{
					year: m === 1 ? y - 1 : y,
					month: m === 1 ? 12 : m - 1,
					day: this.daysInMonth(m === 1 ? 12 : m - 1, m === 1 ? y - 1 : y),
				},
				this.opts.disableUntil,
			);
			dpy = this.utilService.isMonthDisabledByDisableUntil(
				{ year: y - 1, month: m, day: this.daysInMonth(m, y - 1) },
				this.opts.disableUntil,
			);
			dnm = this.utilService.isMonthDisabledByDisableSince(
				{ year: m === 12 ? y + 1 : y, month: m === 12 ? 1 : m + 1, day: 1 },
				this.opts.disableSince,
			);
			dny = this.utilService.isMonthDisabledByDisableSince({ year: y + 1, month: m, day: 1 }, this.opts.disableSince);
		}
		this.prevMonthDisabled = (m === 1 && y === this.opts.minYear) || dpm;
		this.prevYearDisabled = y - 1 < this.opts.minYear || dpy;
		this.nextMonthDisabled = (m === 12 && y === this.opts.maxYear) || dnm;
		this.nextYearDisabled = y + 1 > this.opts.maxYear || dny;
	}

	isCurrentWeek(data: { weekNbr: number; week: { dateObj: { year: number } }[] }) {
		const year = new Date().getFullYear();

		return data.weekNbr === this.currWeek && data.week.some((x) => x.dateObj.year === year);
	}

	isSelectedWeek(data: { weekNbr: number; week: { dateObj: { year: number } }[] }) {
		return (
			this.opts.markCurrentWeek
			&& this.opts.selectWeekOnly
			&& data.weekNbr === this.selectedDate.week
			&& data.week.some((x) => x.dateObj.year === this.selectedDate.year)
		);
	}
}
